<template>
  <div class="login-page">
    <!-- Left panel -->
    <div class="login-left">
      <AuthLeftPanel />
    </div>

    <!-- Right panel -->
    <div class="login-right">
      <div class="login-box">
        <div v-if="step === 'LOADING'" class="text-center py-5">
          <b-spinner variant="primary" />
        </div>

        <!-- Алхам 1: Имэйл -->
        <b-form v-else-if="step === 'NEEDS_EMAIL'" @submit.prevent="submitEmail">
          <h5 class="form-title text-heading">{{ $t("dan_setup.title") }}</h5>
          <p class="form-subtitle text-body-sm">
            {{ $t("dan_setup.email_description", { name: firstname || "" }) }}
          </p>
          <UITextField v-model="email" type="email" :label="$t('dan_setup.email_placeholder')" />
          <b-alert :show="!!errorMsg" variant="danger" class="mt-2">{{ errorMsg }}</b-alert>
          <UIButton
            type="submit"
            :label="$t('dan_setup.email_submit')"
            variant="primary"
            :block="true"
            :loading="loading"
            class="mt-3"
          />
        </b-form>

        <!-- Алхам 2: Имэйлийн OTP -->
        <template v-else-if="step === 'NEEDS_EMAIL_CODE'">
          <h5 class="form-title text-heading">{{ $t("dan_setup.title") }}</h5>
          <p class="otp-hint">{{ $t("dan_setup.otp_hint", { email }) }}</p>
          <b-form @submit.prevent="submitOtp">
            <UITextField v-model="otp" type="text" :label="$t('dan_setup.otp_placeholder')" />
            <b-alert :show="!!errorMsg" variant="danger" class="mt-2">{{ errorMsg }}</b-alert>
            <UIButton
              type="submit"
              :label="$t('dan_setup.otp_submit')"
              variant="primary"
              :block="true"
              :loading="loading"
              class="mt-3"
            />
          </b-form>
          <button class="resend-btn" :disabled="loading" @click="submitEmail">
            {{ $t("dan_setup.resend") }}
          </button>
          <button class="resend-btn" :disabled="loading" @click="changeEmail">
            {{ $t("dan_setup.change_email") }}
          </button>
        </template>

        <!-- Алхам 3: Утас + нууц үг -->
        <b-form v-else-if="step === 'NEEDS_PASSWORD'" @submit.prevent="submitComplete">
          <h5 class="form-title text-heading">{{ $t("dan_setup.title") }}</h5>
          <p class="form-subtitle text-body-sm">{{ $t("dan_setup.password_description") }}</p>
          <UITextField v-model="mobilenumber" type="text" :label="$t('dan_setup.phone_placeholder')" />
          <label class="text-label">{{ $t("forgot_password.password_label") }}</label>
          <UITextField
            v-model="password"
            type="password"
            autocomplete="new-password"
            :label="$t('forgot_password.password_placeholder')"
          />
          <label class="text-label">{{ $t("forgot_password.password_confirm_label") }}</label>
          <UITextField
            v-model="passwordConfirm"
            type="password"
            autocomplete="new-password"
            :label="$t('forgot_password.password_placeholder')"
          />
          <ul class="password-requirements">
            <li
              v-for="req in passwordRequirements"
              :key="req.key"
              :class="req.met ? 'requirement-met' : 'requirement-unmet'"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              {{ req.label }}
            </li>
          </ul>
          <b-alert :show="!!errorMsg" variant="danger" class="mt-2">{{ errorMsg }}</b-alert>
          <UIButton
            type="submit"
            :label="$t('dan_setup.complete_submit')"
            variant="primary"
            :block="true"
            :loading="loading"
            class="mt-3"
          />
        </b-form>

        <!-- Үр дүн / алдааны дэлгэцүүд -->
        <template v-else>
          <h5 class="form-title text-heading">{{ $t("dan_setup.title") }}</h5>
          <b-alert show :variant="resultVariant" class="mb-3">{{ resultMessage }}</b-alert>
          <p v-if="step === 'HAS_EMAIL' && (broker.phone || broker.email)" class="broker-contact">
            {{ $t("dan_setup.broker_contact") }}
            <span v-if="broker.phone">{{ $t("dan_setup.broker_phone") }}: {{ broker.phone }}</span>
            <span v-if="broker.email">{{ $t("dan_setup.broker_email") }}: {{ broker.email }}</span>
          </p>

          <UIButton
            v-if="step === 'HAS_EMAIL'"
            :label="$t('login.forgot_password')"
            variant="primary"
            :block="true"
            class="mb-2"
            @click="$router.push('/user/forgot-password')"
          />
          <UIButton
            v-if="step === 'ERROR'"
            :label="$t('dan_setup.restart')"
            variant="primary"
            :block="true"
            :loading="loading"
            class="mb-2"
            @click="restart"
          />
        </template>

        <template v-if="step !== 'LOADING'">
          <div class="login-divider text-body-xs">
            <span>{{ $t("login.or") || "эсвэл" }}</span>
          </div>
          <UIButton
            :label="$t('forgot_password.back_to_login')"
            variant="secondary"
            :block="true"
            @click="goToLogin"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import UIButton from "../../components/UI/UIButton.vue";
import AuthLeftPanel from "../../components/UI/AuthLeftPanel.vue";
import UITextField from "../../components/UI/UITextField.vue";
import danApi, { DAN_SETUP_KEY, DAN_SETUP_STATE_KEY } from "../../api/danApi";
import { CompanyInfo } from "../../utils/constants";

// Эдгээр алдааны үед setup дахин ашиглагдахгүй — e-Mongolia-аар дахин эхлэх хэрэгтэй
const RESTART_CODES = ["SESSION_EXPIRED", "TOO_MANY_ATTEMPTS", "INVALID_CODE", "ALREADY_COMPLETED"];

export default {
  name: "DanSetupPage",
  components: { UIButton, AuthLeftPanel, UITextField },
  data() {
    return {
      // LOADING | NEEDS_EMAIL | NEEDS_EMAIL_CODE | NEEDS_PASSWORD | HAS_EMAIL | CONTACT_SUPPORT | ERROR
      step: "LOADING",
      firstname: null,
      maskedEmail: "",
      email: "",
      otp: "",
      mobilenumber: "",
      password: "",
      passwordConfirm: "",
      loading: false,
      errorMsg: "",
      finalError: "",
      broker: CompanyInfo,
    };
  },
  computed: {
    resultVariant() {
      return this.step === "HAS_EMAIL" ? "warning" : "danger";
    },
    resultMessage() {
      switch (this.step) {
        case "HAS_EMAIL":
          return this.$t("dan_setup.has_email", { email: this.maskedEmail });
        case "CONTACT_SUPPORT":
          return this.$t("dan_setup.contact_support");
        default:
          return this.finalError || this.$t("dan_setup.error_generic");
      }
    },
    passwordRequirements() {
      const pwd = this.password;
      const hasUpper = /[A-Z]/.test(pwd);
      const hasLower = /[a-z]/.test(pwd);
      const hasNumber = /[0-9]/.test(pwd);
      const hasSpecial = /[^A-Za-z0-9]/.test(pwd);
      return [
        { key: "max", label: this.$t("forgot_password.req_max"), met: pwd.length <= 64 },
        { key: "min", label: this.$t("forgot_password.req_min"), met: pwd.length >= 8 },
        {
          key: "case_number",
          label: this.$t("forgot_password.req_case_number"),
          met: hasUpper && hasLower && hasNumber,
        },
        { key: "special", label: this.$t("forgot_password.req_special"), met: hasSpecial },
      ];
    },
  },
  mounted() {
    const { code, state, error } = this.$route.query;
    if (code || error) {
      this.handleDanReturn(code, state, error);
    } else {
      this.resume();
    }
  },
  methods: {
    // e-Mongolia-аас буцаж ирсэн: state шалгаж code-г session key-ээр солино
    async handleDanReturn(code, state, error) {
      const savedState = sessionStorage.getItem(DAN_SETUP_STATE_KEY);
      sessionStorage.removeItem(DAN_SETUP_STATE_KEY);
      this.$router.replace({ query: {} });

      if (!savedState || savedState !== state) {
        return this.showError(this.$t("dan_setup.invalid_link"));
      }
      if (error) {
        return this.showError(
          this.$t(error === "DAN_FAILED" ? "dan_setup.dan_failed" : "dan_setup.error_generic"),
        );
      }
      try {
        const { data } = await danApi.exchangeSetupCode(code, state);
        if (data.key) {
          sessionStorage.setItem(DAN_SETUP_KEY, data.key);
        }
        this.firstname = data.firstname || null;
        this.maskedEmail = data.masked_email || "";
        this.step = data.step;
      } catch (err) {
        this.handleError(err);
      }
    },
    // Хуудас refresh хийсэн: хадгалсан key-ээр алхмаа сэргээнэ
    async resume() {
      const key = sessionStorage.getItem(DAN_SETUP_KEY);
      if (!key) {
        return this.showError(this.$t("dan_setup.session_missing"));
      }
      try {
        const { data } = await danApi.getSetupStatus(key);
        this.firstname = data.firstname || null;
        this.email = data.email || "";
        this.step = data.step;
      } catch (err) {
        this.handleError(err);
      }
    },
    async submitEmail() {
      await this.request(async (key) => {
        const { data } = await danApi.sendSetupEmailOtp(key, this.email.trim());
        this.email = data.email;
        this.otp = "";
        this.step = data.step;
      });
    },
    changeEmail() {
      this.errorMsg = "";
      this.step = "NEEDS_EMAIL";
    },
    async submitOtp() {
      await this.request(async (key) => {
        const { data } = await danApi.confirmSetupEmailOtp(key, this.otp.trim());
        this.step = data.step;
      });
    },
    async submitComplete() {
      this.errorMsg = "";
      if (!/^\d{8}$/.test(this.mobilenumber.trim())) {
        this.errorMsg = this.$t("dan_setup.phone_invalid");
        return;
      }
      if (this.password !== this.passwordConfirm) {
        this.errorMsg = this.$t("forgot_password.password_mismatch");
        return;
      }
      if (!this.passwordRequirements.every((req) => req.met)) {
        this.errorMsg = this.$t("forgot_password.password_complexity");
        return;
      }
      await this.request(async (key) => {
        const { data } = await danApi.completeSetup(key, {
          mobilenumber: this.mobilenumber.trim(),
          password: this.password,
          password_confirmation: this.passwordConfirm,
        });
        sessionStorage.removeItem(DAN_SETUP_KEY);
        this.$router.push({ name: "login", query: { dan: "success", email: data.email } });
      });
    },
    async request(action) {
      const key = sessionStorage.getItem(DAN_SETUP_KEY);
      if (!key) {
        return this.showError(this.$t("dan_setup.session_missing"));
      }
      this.errorMsg = "";
      this.loading = true;
      try {
        await action(key);
      } catch (err) {
        this.handleError(err);
      } finally {
        this.loading = false;
      }
    },
    handleError(err) {
      const data = err?.response?.data;
      if (!data || RESTART_CODES.includes(data.code) || this.step === "LOADING") {
        return this.showError(data?.message);
      }
      this.errorMsg = data.message || this.$t("dan_setup.error_generic");
    },
    showError(message) {
      sessionStorage.removeItem(DAN_SETUP_KEY);
      this.finalError = message || this.$t("dan_setup.error_generic");
      this.step = "ERROR";
    },
    async restart() {
      this.loading = true;
      try {
        await danApi.startVerify();
      } catch (err) {
        this.loading = false;
        this.finalError = this.$t("dan_setup.error_generic");
      }
    },
    goToLogin() {
      sessionStorage.removeItem(DAN_SETUP_KEY);
      this.$router.push("/user/login");
    },
  },
};
</script>

<style scoped>
/* ── Password requirements ── */
.password-requirements {
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
}

.password-requirements li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  padding: 4px 0;
}

.password-requirements li svg {
  flex-shrink: 0;
}

.requirement-met {
  color: #2f9e44;
}

.requirement-unmet {
  color: #e03131;
}

/* ── Layout ── */
.login-page {
  min-height: 100vh;
  display: flex;
}

.login-left {
  flex: 0 0 50%;
  overflow: hidden;
}

.login-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  padding: 10%;
  padding-top: 100px;
  overflow-y: auto;
  overflow-x: hidden;
}

@media (max-width: 768px) {
  .login-left {
    display: none;
  }
  .login-right {
    width: 100%;
  }
}

.login-box {
  width: 100%;
  max-width: 350px;
}

/* ── Titles ── */
.form-title {
  color: #1a2035;
  margin-bottom: 6px;
}
.form-subtitle {
  color: #71717a !important;
  margin-bottom: 24px;
  line-height: 1.5;
}

label {
  color: #555e6d;
  font-size: 13px;
  margin-bottom: 4px;
  display: block;
}

.broker-contact {
  display: grid;
  gap: 2px;
  font-size: 13px;
  color: #555e6d;
  margin-bottom: 16px;
}

.otp-hint {
  color: #555e6d;
  font-size: 13px;
  margin-bottom: 16px;
  text-align: center;
}

.resend-btn {
  display: block;
  width: 100%;
  margin-top: 8px;
  background: none;
  border: none;
  color: #aab0bc;
  font-size: 12px;
  cursor: pointer;
  text-align: center;
  padding: 6px 0;
}
.resend-btn:hover {
  color: #2d3f9c;
}

/* ── Divider ── */
.login-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0;
  color: #aab0bc !important;
}
.login-divider::before,
.login-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #e4e7ef;
}
</style>
